import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Banner, Button } from '@elysian/ui';

/**
 * If a screen fails while showing, say so in its place, keeping the menu,
 * instead of leaving the whole app blank until a refresh (#24). Going to
 * another screen (resetKey changes) clears it.
 */
export class ScreenErrorBoundary extends Component<{ resetKey: string; children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Screen failed:', error, info.componentStack);
  }

  componentDidUpdate(prev: { resetKey: string }) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" style={{ maxWidth: 720 }}>
        <Banner action={<Button size="sm" onClick={() => this.setState({ error: null })}>Try again</Button>}>
          This screen couldn’t be shown. The rest of the app still works: choose another screen from the menu, or try again.
          If it keeps happening, please raise an issue with this message: “{this.state.error.message}”.
        </Banner>
      </div>
    );
  }
}
